import { Link } from 'react-router-dom'
import { Card } from '@/components/ui/Card'
import { Badge } from '@/components/ui/Badge'
import { Spinner } from '@/components/ui/Spinner'
import { EmptyState } from '@/components/ui/EmptyState'
import { Button } from '@/components/ui/Button'
import { useProjectAssistanceRequests } from '@/hooks/useAssistance'
import {
  ASSISTANCE_STATUS_CONFIG,
  PRIORITY_CONFIG,
  ASSISTANCE_CATEGORY_LABELS,
} from '@/types/assistance'
import {
  Wrench,
  Clock,
  CheckCircle,
  ExternalLink,
} from 'lucide-react'
import { formatDistanceToNow } from 'date-fns'
import { fr } from 'date-fns/locale'

interface AssistanceTabProps {
  projectId: string
}

export function AssistanceTab({ projectId }: AssistanceTabProps) {
  const { data: requests, isLoading } = useProjectAssistanceRequests(projectId)

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    )
  }

  const submitted = requests?.filter(r => r.status === 'submitted') || []
  const inProgress = requests?.filter(r => r.status === 'in_progress' || r.status === 'approved') || []
  const completed = requests?.filter(r => r.status === 'completed' || r.status === 'billed') || []

  if (!requests || requests.length === 0) {
    return (
      <Card padding="lg">
        <EmptyState
          icon={<Wrench className="w-12 h-12" />}
          title="Aucune demande d'assistance"
          description="Ce projet n'a pas encore de demandes d'assistance."
        />
      </Card>
    )
  }

  return (
    <div className="space-y-4">
      {/* Stats pills */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-50 rounded-full">
          <Wrench className="w-4 h-4 text-purple-500" />
          <span className="text-sm font-medium text-purple-700">{submitted.length}</span>
          <span className="text-xs text-purple-500">soumises</span>
        </div>
        <div className="flex items-center gap-1.5 px-3 py-1.5 bg-gold-50 rounded-full">
          <Clock className="w-4 h-4 text-gold-500" />
          <span className="text-sm font-medium text-gold-700">{inProgress.length}</span>
          <span className="text-xs text-gold-500">en cours</span>
        </div>
        <div className="flex items-center gap-1.5 px-3 py-1.5 bg-mint-50 rounded-full">
          <CheckCircle className="w-4 h-4 text-mint-500" />
          <span className="text-sm font-medium text-mint-700">{completed.length}</span>
          <span className="text-xs text-mint-500">terminées</span>
        </div>
      </div>

      {/* Requests list */}
      <div className="space-y-2">
        {requests.map((req) => (
          <div
            key={req.id}
            className="flex items-start gap-3 p-4 rounded-lg border border-beige-200 bg-white hover:border-beige-300 transition-colors"
          >
            <Wrench className="w-5 h-5 text-purple-500 mt-0.5 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <h4 className="font-medium text-brown-900">{req.title}</h4>
                  <p className="text-sm text-brown-500 mt-0.5 line-clamp-1">{req.description}</p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <Badge variant={PRIORITY_CONFIG[req.priority]?.color} size="sm">
                    {PRIORITY_CONFIG[req.priority]?.label}
                  </Badge>
                  <Badge variant={ASSISTANCE_STATUS_CONFIG[req.status]?.color} size="sm">
                    {ASSISTANCE_STATUS_CONFIG[req.status]?.label}
                  </Badge>
                </div>
              </div>
              <div className="flex items-center gap-4 mt-2 text-xs text-brown-500">
                <span>{ASSISTANCE_CATEGORY_LABELS[req.category] || req.category}</span>
                {req.is_covered === true && (
                  <Badge variant="mint" size="sm">Couvert</Badge>
                )}
                {req.is_covered === false && (
                  <Badge variant="gold" size="sm">Facturable</Badge>
                )}
                <span>
                  {formatDistanceToNow(new Date(req.created_at), { addSuffix: true, locale: fr })}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Link to full list */}
      <div className="flex justify-center pt-2">
        <Link to={`/assistance?projectId=${projectId}`}>
          <Button variant="outline" size="sm">
            <ExternalLink className="w-4 h-4 mr-1.5" />
            Voir toutes les demandes
          </Button>
        </Link>
      </div>
    </div>
  )
}

export default AssistanceTab
